import React, { useEffect, useRef } from 'react';
import { Textarea, TextInput } from '@mantine/core';

import { registerPuckRichTextFlusher } from '../../utils/puckRichTextSyncRegistry';
import { useDebouncedFieldValue } from './useDebouncedFieldValue';

/**
 * Champ texte Puck débouncé : le champ natif (contrôlé) dispatch un `replace`
 * dans le store Puck à CHAQUE frappe → re-résolution + re-rendu canvas, lag
 * sur les grandes pages. Ici la frappe vit en état local ; le store n'est
 * écrit qu'à la pause (400 ms) ou au blur.
 */
export function DebouncedTextInput({
  value, onChange, label, placeholder, description, transform, readOnly, multiline,
}) {
  /* La normalisation s'applique à la valeur ÉMISE, pas à la frappe : slugifier
     en direct retirerait l'espace que l'utilisateur vient de taper. Ce qui est
     stocké est donc la valeur nettoyée, pas ce qui a été collé ; hors focus,
     l'effet de resync ci-dessous réaffiche cette valeur normalisée. */
  const emit = (raw) => onChange(transform ? transform(raw) : raw);

  const {
    local, change, focus, endFocus, flushPending,
  } = useDebouncedFieldValue(value, emit);

  /* Le registre de flush n'est pas réservé à Tiptap : un flusher est juste une
     fonction. Sans lui, une sauvegarde déclenchée moins de 400 ms après la
     frappe (changement de page, Retour, Publier) partait sans cette valeur.
     `flushPending` change d'identité à chaque rendu : on passe par une ref,
     sinon le flusher enregistré au montage appellerait une version périmée. */
  const flushRef = useRef(flushPending);
  flushRef.current = flushPending;
  useEffect(() => registerPuckRichTextFlusher(() => flushRef.current()), []);

  const Input = multiline ? Textarea : TextInput;
  return (
    <Input
      {...(multiline ? { autosize: true, minRows: 2 } : {})}
      size="xs"
      readOnly={readOnly}
      label={label}
      placeholder={placeholder}
      description={description}
      value={local}
      onFocus={focus}
      onBlur={(e) => {
        endFocus();
        // Écrire une valeur identique remonterait Puck pour rien.
        if (e.currentTarget.value !== (value ?? '')) emit(e.currentTarget.value);
      }}
      onChange={(e) => change(e.currentTarget.value)}
    />
  );
}

/**
 * @param {string} label
 * @param {{placeholder?: string, description?: string, multiline?: boolean,
 *          transform?: (raw: string) => string}} [options]
 *   `transform` normalise la valeur au moment de l'écrire : slug d'ancre, ou
 *   adresse extraite quand un service ne propose de copier qu'un extrait HTML
 *   — le balisage ne descend alors jamais en base.
 */
export function puckDebouncedTextField(label, {
  placeholder, description, transform, multiline,
} = {}) {
  return {
    type: 'custom',
    label,
    render: ({ value, onChange }) => (
      <DebouncedTextInput
        value={value}
        onChange={onChange}
        label={label}
        placeholder={placeholder}
        description={description}
        transform={transform}
        multiline={multiline}
      />
    ),
  };
}
